import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { AuthProvider, type AuthProviderProps, hasAuthParams, useAuth } from 'react-oidc-context';
import { StatusScreen } from '../components/status-screen';
import { Button } from '../components/ui/button';
import { type CognitoConfig, type DevClaims, useConfig } from './config';
import {
  claimsFromObject,
  decodeJwtPayload,
  LOCAL_CLAIMS,
  type SessionClaims,
} from './tenant-role';

/**
 * Authentication has three modes, chosen by `config.cognito` / `config.devClaims`:
 *
 * - `cognito: null, devClaims: null` → "auth disabled (local development)": everyone is let
 *   through, the shell shows a banner, and the API client sends no Authorization header.
 * - `cognito: null, devClaims: {...}` → dev-claims mode (local dev + Playwright in CI): the
 *   claims are treated as a signed-in session and sent as `x-dev-claims` (the API only honors
 *   the header with `ALLOW_DEV_CLAIMS=true`, never in AWS).
 * - `cognito: {...}` → Cognito OAuth 2.0 authorization code + PKCE via react-oidc-context. Every
 *   route under the app layout is wrapped in `<AuthGate>`; `/callback` and `/signed-out` are not.
 *
 * Components never touch react-oidc-context directly; they read the normalized `Session`.
 */

export type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error';

export interface SessionUser {
  sub: string | null;
  email: string | null;
  name: string | null;
}

export interface Session {
  /** `false` when `config.cognito` is null (auth disabled or dev-claims mode). */
  authEnabled: boolean;
  status: SessionStatus;
  error: Error | null;
  user: SessionUser | null;
  /** Tenant id / user id / tenant role from the token (or dev claims); UI gating only. */
  claims: SessionClaims | null;
  /** Set in dev-claims mode; the API client sends it as `x-dev-claims`. */
  devClaims: DevClaims | null;
  /** Current access token (reactive). `null` when anonymous or auth is disabled. */
  accessToken: string | null;
  /** True while oidc-client-ts is redirecting the browser (sign-in/sign-out in flight). */
  isNavigating: boolean;
  /** Same-origin path carried through the sign-in round-trip, e.g. `/projects?x=1`. */
  returnTo: string | null;
  /** Stable getter used by the API client; always returns the latest token. */
  getAccessToken: () => string | null;
  /** Starts the redirect to Cognito. `returnTo` defaults to the current location. */
  signIn: (returnTo?: string) => Promise<void>;
  /** Clears the local session and, when the IdP supports it, redirects to its logout endpoint. */
  signOut: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (session === null) {
    throw new Error('useSession() must be called inside <SessionProvider>');
  }
  return session;
}

/** Reactive access token; `null` in local development mode. */
export function useAccessToken(): string | null {
  return useSession().accessToken;
}

/**
 * Tenant scope for query keys and role gating. Falls back to the local placeholder when auth is
 * disabled, so hooks always have a tenant id to namespace with.
 */
export function useSessionClaims(): SessionClaims {
  return useSession().claims ?? LOCAL_CLAIMS;
}

const noToken = (): string | null => null;
const noop = (): Promise<void> => Promise.resolve();

const DISABLED_SESSION: Session = {
  authEnabled: false,
  status: 'authenticated',
  error: null,
  user: { sub: null, email: 'dev@localhost', name: 'Local developer' },
  claims: null,
  devClaims: null,
  accessToken: null,
  isNavigating: false,
  returnTo: null,
  getAccessToken: noToken,
  signIn: noop,
  signOut: noop,
};

function devClaimsSession(devClaims: DevClaims): Session {
  return {
    ...DISABLED_SESSION,
    user: {
      sub: devClaims.sub,
      email: devClaims.email ?? null,
      name: devClaims.email ?? 'Dev claims user',
    },
    claims: {
      tenantId: devClaims.tenant_id,
      userId: devClaims.user_id,
      tenantRole: devClaims.tenant_role,
    },
    devClaims,
  };
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const config = useConfig();
  const oidcProps = useMemo(
    () => (config.cognito ? toOidcProps(config.cognito) : null),
    [config.cognito],
  );
  const devSession = useMemo(
    () => (config.cognito === null && config.devClaims ? devClaimsSession(config.devClaims) : null),
    [config.cognito, config.devClaims],
  );

  if (oidcProps === null) {
    return (
      <SessionContext.Provider value={devSession ?? DISABLED_SESSION}>
        {children}
      </SessionContext.Provider>
    );
  }
  return (
    <AuthProvider {...oidcProps}>
      <OidcSessionBridge>{children}</OidcSessionBridge>
    </AuthProvider>
  );
}

function toOidcProps(cognito: CognitoConfig): AuthProviderProps {
  return {
    authority: cognito.authority,
    client_id: cognito.clientId,
    redirect_uri: cognito.redirectUri,
    post_logout_redirect_uri: cognito.postLogoutRedirectUri,
    scope: cognito.scope,
    response_type: 'code',
    automaticSilentRenew: true,
    onSigninCallback: () => {
      // Drop ?code=&state= from the address bar once the token exchange has completed.
      window.history.replaceState({}, document.title, window.location.pathname);
    },
  };
}

/** Adapts react-oidc-context's state to the `Session` shape the rest of the app consumes. */
function OidcSessionBridge({ children }: { children: ReactNode }) {
  const {
    isLoading,
    isAuthenticated,
    error,
    user,
    activeNavigator,
    signinRedirect,
    signoutRedirect,
    removeUser,
  } = useAuth();

  const accessToken = user?.access_token ?? null;
  const tokenRef = useRef<string | null>(accessToken);
  useEffect(() => {
    tokenRef.current = accessToken;
  }, [accessToken]);
  const getAccessToken = useCallback(() => tokenRef.current, []);

  // The pre-token trigger writes tenant_id/user_id/tenant_role into both tokens; prefer the
  // access token (what the API sees), fall back to the id-token profile.
  const claims = useMemo<SessionClaims | null>(() => {
    if (!user) return null;
    const fromAccess = accessToken ? claimsFromObject(decodeJwtPayload(accessToken)) : null;
    return fromAccess ?? claimsFromObject(user.profile);
  }, [user, accessToken]);

  const signIn = useCallback(
    async (returnTo?: string) => {
      const target = returnTo ?? currentPath();
      await signinRedirect({ state: { returnTo: isSafePath(target) ? target : '/' } });
    },
    [signinRedirect],
  );

  const cognito = useConfig().cognito;
  const signOut = useCallback(async () => {
    // Cognito's discovery document has no `end_session_endpoint`, so oidc-client-ts cannot build a
    // logout URL. Clear the local session first, then send the browser to Managed Login's /logout
    // (configured as `cognito.logoutUri`) so the hosted-UI cookie is cleared too.
    await removeUser();
    if (cognito?.logoutUri) {
      const url = new URL(cognito.logoutUri);
      url.searchParams.set('client_id', cognito.clientId);
      url.searchParams.set('logout_uri', cognito.postLogoutRedirectUri);
      window.location.assign(url.toString());
      return;
    }
    try {
      await signoutRedirect();
    } catch {
      // Already signed out locally; nothing else the IdP can do without a logout endpoint.
    }
  }, [cognito, signoutRedirect, removeUser]);

  const session = useMemo<Session>(
    () => ({
      authEnabled: true,
      status: deriveStatus({ isLoading, isAuthenticated, error }),
      error: error ?? null,
      user: user
        ? {
            sub: user.profile.sub,
            email: user.profile.email ?? null,
            name: user.profile.name ?? null,
          }
        : null,
      claims,
      devClaims: null,
      accessToken,
      isNavigating: activeNavigator !== undefined,
      returnTo: readReturnTo(user?.state),
      getAccessToken,
      signIn,
      signOut,
    }),
    [
      accessToken,
      activeNavigator,
      claims,
      error,
      getAccessToken,
      isAuthenticated,
      isLoading,
      signIn,
      signOut,
      user,
    ],
  );

  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}

function deriveStatus(args: {
  isLoading: boolean;
  isAuthenticated: boolean;
  error: Error | undefined;
}): SessionStatus {
  if (args.error) return 'error';
  if (args.isLoading) return 'loading';
  return args.isAuthenticated ? 'authenticated' : 'unauthenticated';
}

function currentPath(): string {
  const { pathname, search, hash } = window.location;
  if (pathname === '/callback' || pathname === '/signed-out') return '/';
  return `${pathname}${search}${hash}`;
}

/** Only same-origin absolute paths may be used as post-login targets (no open redirects). */
export function isSafePath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\');
}

function readReturnTo(state: unknown): string | null {
  if (typeof state !== 'object' || state === null || !('returnTo' in state)) return null;
  const value = state.returnTo;
  return typeof value === 'string' && isSafePath(value) ? value : null;
}

/**
 * Requires a signed-in user for everything it wraps. In local development mode it renders its
 * children immediately. Otherwise it starts the Cognito redirect for anonymous visitors and shows
 * progress/error screens until a session exists.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { authEnabled, status, isNavigating, error, signIn } = useSession();
  const requested = useRef(false);

  useEffect(() => {
    if (!authEnabled || status !== 'unauthenticated' || isNavigating || requested.current) return;
    // The AuthProvider is still exchanging ?code= for tokens; do not start a second flow.
    if (hasAuthParams()) return;
    requested.current = true;
    void signIn();
  }, [authEnabled, status, isNavigating, signIn]);

  if (!authEnabled || status === 'authenticated') return children;

  if (status === 'error') {
    return (
      <StatusScreen
        tone="error"
        title="Sign-in failed"
        description={error?.message ?? 'The identity provider returned an error.'}
        action={<Button onClick={() => void signIn('/')}>Try again</Button>}
      />
    );
  }

  return (
    <StatusScreen
      tone="loading"
      title={status === 'loading' ? 'Checking your session…' : 'Redirecting to sign in…'}
    />
  );
}
